// src/App.tsx

import { useEffect, useState } from 'react';
import { Footer } from './components/Footer';
import { Header } from './components/Header';
import { LoadingSpinner } from './components/LoadingSpinner';
import { ArticlePage } from './Page/ArticlePage';
import { ArticlesPage } from './Page/ArticlesPage';
import { HomePage } from './Page/HomePage';
import { PageView } from './Page/PageView';
import {
  getArticleBySlug,
  getArticles,
  getPageBySlug,
  getPages,
  getSiteConfig,
  isContentfulConfigured
} from './services/contentful';
import { Article, Page, SiteConfig, ViewType } from './types';

// Import custom fonts
import './styles/fonts.css';

export default function App() {
  const [siteConfig, setSiteConfig] = useState<SiteConfig | null>(null);
  const [currentView, setCurrentView] = useState<ViewType>('home');
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [selectedPage, setSelectedPage] = useState<Page | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [articles, setArticles] = useState<Article[]>([]);
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadContent();
  }, []);

  async function loadContent() {
    setLoading(true);
    setError(null);

    try {
      if (!isContentfulConfigured()) {
        setError('Contentful-tunnukset puuttuvat. Päivitä SPACE_ID ja ACCESS_TOKEN .env-tiedostossa.');
        setLoading(false);
        return;
      }

      const [siteConfigData, articlesData, pagesData] = await Promise.all([
        getSiteConfig(),
        getArticles(),
        getPages(),
      ]);

      setSiteConfig(siteConfigData);
      setArticles(articlesData);
      setPages(pagesData);

      // Handle URL routing
      const path = window.location.pathname;
      if (path.startsWith('/article/')) {
        const slug = path.replace('/article/', '');
        const article = await getArticleBySlug(slug);
        if (article) {
          setSelectedArticle(article);
          setCurrentView('article');
        }
      } else if (path !== '/' && path !== '/articles') {
        const slug = path.replace('/', '');
        const page = pagesData.find(p => p.slug === slug);
        if (page) {
          setSelectedPage(page);
          setCurrentView('page');
        }
      }
    } catch (err) {
      setError('Sisällön lataaminen epäonnistui. Tarkista Contentful-asetukset.');
      console.error('Error loading content:', err);
    } finally {
      setLoading(false);
    }
  }

  const handleArticleClick = async (article: Article) => {
    setLoading(true);
    const fullArticle = await getArticleBySlug(article.slug);
    setLoading(false);

    if (fullArticle) {
      setSelectedArticle(fullArticle);
      setCurrentView('article');
      window.history.pushState({}, '', `/article/${article.slug}`);
      setMobileMenuOpen(false);
      window.scrollTo(0, 0);
    }
  };

  const handlePageClick = async (page: Page) => {
    if (!page.assets || page.assets.length === 0) {
      setLoading(true);
      const fullPage = await getPageBySlug(page.slug);
      setLoading(false);
      if (fullPage) {
        setSelectedPage(fullPage);
      } else {
        setSelectedPage(page);
      }
    } else {
      setSelectedPage(page);
    }
    setCurrentView('page');
    window.history.pushState({}, '', `/${page.slug}`);
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const handleHomeClick = () => {
    setCurrentView('home');
    window.history.pushState({}, '', '/');
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const handleArticlesClick = () => {
    setCurrentView('articles');
    window.history.pushState({}, '', '/articles');
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const handleBackToArticles = () => {
    setCurrentView('articles');
  };

  const featuredArticles = articles.filter(a => a.isFeatured).slice(0, 3);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        siteConfig={siteConfig}
        pages={pages}
        mobileMenuOpen={mobileMenuOpen}
        onToggleMobileMenu={() => setMobileMenuOpen(!mobileMenuOpen)}
        onHomeClick={handleHomeClick}
        onArticlesClick={handleArticlesClick}
        onPageClick={handlePageClick}
      />

      <main className="max-w-7xl mx-auto px-6 lg:px-12 py-16">
        {loading && <LoadingSpinner />}

        {error && (
          <div className="bg-yellow-50 border-l-8 border-yellow-500 p-8 mb-12 shadow-lg">
            <h3 className="text-yellow-900 font-display font-black text-3xl mb-3 uppercase tracking-tight">
              Virhe
            </h3>
            <p className="text-yellow-800 text-lg font-body">{error}</p>
          </div>
        )}

        {!loading && !error && (
          <>
            {currentView === 'home' && (
              <HomePage
                featuredArticles={featuredArticles}
                onArticleClick={handleArticleClick}
                onArticlesClick={handleArticlesClick}
              />
            )}

            {currentView === 'articles' && (
              <ArticlesPage
                articles={articles}
                onArticleClick={handleArticleClick}
              />
            )}

            {currentView === 'article' && selectedArticle && (
              <ArticlePage
                article={selectedArticle}
                onBackClick={handleBackToArticles}
              />
            )}

            {currentView === 'page' && selectedPage && (
              <PageView page={selectedPage} />
            )}
          </>
        )}
      </main>

      <Footer siteConfig={siteConfig} />
    </div>
  );
}